import { IconDeviceFloppy, IconLoader2 } from "@tabler/icons-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { type BookingSeasonSnapshot, useSaveBookingSeasonMutation } from "../bookingSeasonApi"
import { closedReasonLabel, formatSeasonDateTime } from "../bookingSeasonFormat"
import { buildSaveRequest, isSaveRefusal, type SeasonFormState, statusAfterSave, validateSeasonForm } from "../lib/seasonForm"

interface SeasonSaveBarProps {
  form: SeasonFormState
  /** True when the form still matches the current season. */
  unchanged: boolean
  /** Whether a save has been tried since the last save or discard: field issues show from then on. */
  attempted: boolean
  onAttemptedChange: (attempted: boolean) => void
  /** The reasons the server last refused a save; cleared by the page on any edit. */
  serverErrors: string[]
  onServerErrors: (errors: string[]) => void
  onReset: () => void
  /** Called with the saved season, so the page can follow it. */
  onSaved: (season: BookingSeasonSnapshot) => void
}

/**
 * One save for the whole season — booking window, appointments window and closed messages go in a
 * single PUT, which appends one row, so the season's history never holds half an edit.
 */
export function SeasonSaveBar({
  form,
  unchanged,
  attempted,
  onAttemptedChange,
  serverErrors,
  onServerErrors,
  onReset,
  onSaved,
}: SeasonSaveBarProps) {
  const [save, { isLoading: saving }] = useSaveBookingSeasonMutation()

  const issues = validateSeasonForm(form)
  const request = buildSaveRequest(form)
  const willBe = request ? statusAfterSave(request, new Date()) : null

  async function handleSave() {
    onAttemptedChange(true)
    onServerErrors([])
    if (!request) return

    try {
      const saved = await save(request).unwrap()
      onAttemptedChange(false)
      onSaved(saved)
      toast.success("Booking season saved.", {
        description:
          saved.status === "Open" && saved.closesAt
            ? `Public booking is open until ${formatSeasonDateTime(saved.closesAt)}.`
            : `Public booking is closed (${closedReasonLabel(saved) ?? "Closed"}).`,
      })
    } catch (err) {
      const error = err as { status?: number; data?: unknown }

      if (error.status === 400 && isSaveRefusal(error.data)) {
        onServerErrors(error.data.errors)
      } else if (error.status === 403) {
        onServerErrors(["You do not have permission to change the booking season."])
      } else {
        onServerErrors(["Could not save the booking season. Try again in a moment."])
      }
    }
  }

  function handleDiscard() {
    onAttemptedChange(false)
    onServerErrors([])
    onReset()
  }

  // Sticky, so the one save stays in reach whichever card is being edited.
  return (
    <Card className="sticky bottom-4 z-10 shadow-md">
      <CardContent className="flex flex-col gap-3">
        {serverErrors.length > 0 && (
          <div className="flex flex-col gap-1 rounded-lg border border-destructive/50 p-3 text-sm text-destructive" role="alert">
            <span className="font-medium">The season was not saved:</span>
            <ul className="list-disc pl-5">
              {serverErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {unchanged
              ? "No changes to save."
              : attempted && issues.length > 0
                ? "Fix the problems above to save."
                : willBe === "Open"
                  ? "Once saved, public booking will be open. Saving replaces the current season straight away."
                  : willBe === "Closed"
                    ? "Once saved, public booking will be closed. Saving replaces the current season straight away."
                    : null}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleDiscard} disabled={unchanged || saving}>
              Discard changes
            </Button>
            <Button onClick={() => void handleSave()} disabled={unchanged || saving}>
              {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconDeviceFloppy className="size-4" />}
              Save season
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
